import React, { useState, useRef, useEffect } from "react";
import { Star, Bookmark, FileQuestion, BookOpen, ChevronLeft, ChevronRight, Share2, Eye, Grid, List as ListIcon, Pencil } from "lucide-react";
import ShareMenu from "./ShareMenu";
import ResourceSimpleItem from "./ResourceSimpleItem";
import MaterialCover from "./MaterialCover";
import formatCount from "../../utils/formatCount";

const typeBadgeColor = {
  book: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  note: "bg-blue-500/10 text-blue-600 border-blue-500/20",
  quiz: "bg-purple-500/10 text-purple-600 border-purple-500/20",
  exercise: "bg-amber-500/10 text-amber-600 border-amber-500/20",
};

const typeLabel = {
  book: "Book", note: "Notes", quiz: "Quiz", exercise: "Exercise",
};

const ITEMS_PER_PAGE = 9;

const LibraryFeed = ({ resources, saved, onSave, onOpen, onCreateQuiz, viewMode = 'list', onViewModeChange, title = 'All Resources', onViewAll, currentUserId, onEdit }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const [shareMenuId, setShareMenuId] = useState(null);
  const [bookmarkMenuId, setBookmarkMenuId] = useState(null);
  const feedRef = useRef(null);
  const activeMenuRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(resources.length / ITEMS_PER_PAGE));
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const pageResources = resources.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [resources.length]);

  const changePage = (page) => {
    setTransitioning(true);
    setTimeout(() => {
      setCurrentPage(page);
      setTransitioning(false);
      feedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activeMenuRef.current && !activeMenuRef.current.contains(e.target)) {
        closeMenus();
      }
    };
    if (shareMenuId || bookmarkMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [shareMenuId, bookmarkMenuId]);

  const closeMenus = () => { setShareMenuId(null); setBookmarkMenuId(null); };

  return (
    <div ref={feedRef} className="mt-12">
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-title text-base">{title}</h3>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{resources.length} resources</span>
          {onViewAll && (
            <button
              onClick={onViewAll}
              className="text-[11px] font-bold text-primary hover:underline bg-transparent border-none cursor-pointer flex items-center gap-1"
            >
              View All <ChevronRight className="w-3 h-3" />
            </button>
          )}
          {onViewModeChange && (
            <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg">
              <button
                onClick={() => onViewModeChange('grid')}
                className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${viewMode === 'grid' ? 'bg-background text-primary shadow-lg' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}
                title="Grid View"
              >
                <Grid size={13} />
              </button>
              <button
                onClick={() => onViewModeChange('list')}
                className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${viewMode === 'list' ? 'bg-background text-primary shadow-lg' : 'text-muted-foreground hover:text-foreground hover:bg-muted/20'}`}
                title="List View"
              >
                <ListIcon size={13} />
              </button>
            </div>
          )}
        </div>
      </div>
      <div
        className={`transition-all duration-200 ease-out ${
          transitioning ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"
        } ${viewMode === 'list' ? 'flex flex-col gap-2' : 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4'}`}
      >
        {pageResources.map((r, i) => {
          const cardKey = `feed-${r.id}`;
          const isSharing = shareMenuId === cardKey;
          return (
            viewMode === 'list' ? (
              <div key={r.id} className="relative">
                <ResourceSimpleItem
                    item={r}
                    isSaved={saved.includes(r.id)}
                    onOpen={onOpen}
                    onSave={onSave}
                    onShare={() => setShareMenuId(cardKey)}
                    onEdit={currentUserId && r.owner_id === currentUserId ? () => onEdit?.(r) : undefined}
                />
                {isSharing && (
                  <div ref={activeMenuRef} className="absolute top-1/2 -translate-y-1/2 right-[120px] z-[100] animate-in fade-in zoom-in-95 slide-in-from-right-4 duration-300 ease-out">
                    <ShareMenu
                      resourceId={r.id}
                      resourceTitle={r.title}
                      onClose={() => setShareMenuId(null)}
                      position="left"
                    />
                  </div>
                )}
              </div>
            ) : (
            <div
            key={r.id}
            className={`group relative bg-card rounded-2xl transition-all duration-500 hover:-translate-y-2 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_30px_60px_rgba(15,23,42,0.1)] border border-border ${shareMenuId === cardKey ? 'z-[100]' : 'z-10'}`}
          >
            <button
              onClick={() => onOpen(r.id)}
              className="w-full aspect-[16/9] relative overflow-hidden border-none p-0 bg-transparent cursor-pointer"
            >
              <MaterialCover item={r} iconSize={36} hover />
              <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>

            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded-lg uppercase tracking-wider border ${typeBadgeColor[r.type] || 'bg-muted/30 text-muted-foreground/80'}`}>
                  {typeLabel[r.type]}
                </span>
                <div className="flex items-center gap-1 ml-auto text-muted-foreground/80 font-bold">
                  <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                  <span className="text-[11px]">{r.rating}</span>
                </div>
              </div>

              <button onClick={() => onOpen(r.id)} className="block text-left w-full mb-1.5">
                <h4 className="text-[13px] font-bold text-foreground group-hover:text-primary transition-colors truncate">
                  {r.title}
                </h4>
              </button>
              <p className="text-[11px] text-muted-foreground line-clamp-2 leading-relaxed mb-3 h-8">
                {r.excerpt}
              </p>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-[9px] text-muted-foreground/60 font-bold uppercase tracking-wider">
                  <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {r.pages}p</span>
                  <span className="flex items-center gap-1" title={`${r.views || 0} views`}><Eye className="w-3 h-3" /> {formatCount(r.views)}</span>
                </div>

                <div className="flex items-center gap-1.5">
                  {currentUserId && r.owner_id === currentUserId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onEdit?.(r); }}
                      className="w-8 h-8 rounded-xl transition-all flex items-center justify-center border bg-card text-muted-foreground/60 border-border/50 hover:text-foreground hover:border-border"
                      title="Edit material"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => onSave?.(r.id)}
                    className={`w-8 h-8 rounded-xl transition-all flex items-center justify-center border ${
                      saved.includes(r.id) ? "bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20" : "bg-card text-muted-foreground/60 border-border/50 hover:text-foreground hover:border-border"
                    }`}
                    title={saved.includes(r.id) ? "Remove from My Notes" : "Save to My Notes"}
                  >
                    <Bookmark className={`w-3.5 h-3.5 ${saved.includes(r.id) ? "fill-current" : ""}`} />
                  </button>
                </div>
              </div>

              {/* Advanced hover actions */}
              <div className="absolute top-6 left-6 flex flex-col gap-3 opacity-0 group-hover:opacity-100 transition-all duration-700 -translate-x-6 group-hover:translate-x-0 z-[40]">
                <button
                  onClick={() => onCreateQuiz?.(r.id)}
                  className="w-11 h-11 rounded-2xl bg-card/95 backdrop-blur-2xl text-muted-foreground/80 hover:text-primary shadow-[0_10px_40px_rgba(0,0,0,0.08)] border border-border/60 flex items-center justify-center transition-all hover:scale-110 active:scale-95 group/btn-action"
                  title="Create Quiz"
                >
                  <FileQuestion className="w-5 h-5 transition-transform group-active/btn-action:scale-90" />
                </button>
                <div className="relative">
                  <button
                    onClick={() => { closeMenus(); setShareMenuId(shareMenuId === cardKey ? null : cardKey); }}
                    className={`w-11 h-11 rounded-2xl transition-all shadow-[0_10px_40px_rgba(0,0,0,0.08)] border flex items-center justify-center hover:scale-110 active:scale-95 group/btn-action ${
                      shareMenuId === cardKey ? "bg-primary text-primary-foreground border-primary" : "bg-card/95 backdrop-blur-2xl text-muted-foreground/80 border-border/60 hover:text-primary"
                    }`}
                    title="Share"
                  >
                    <Share2 className="w-5 h-5 transition-transform group-active/btn-action:scale-90" />
                  </button>
                  {shareMenuId === cardKey && (
                    <div ref={activeMenuRef} className="absolute top-1/2 -translate-y-1/2 left-full ml-4 z-[100] animate-in fade-in zoom-in-95 slide-in-from-left-4 duration-300 ease-out">
                      <ShareMenu
                        resourceId={r.id}
                        resourceTitle={r.title}
                        onClose={() => setShareMenuId(null)}
                        position="right"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
            )
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => changePage(currentPage - 1)}
            disabled={currentPage === 1}
            className="h-9 px-4 rounded-xl bg-card/5 text-muted-foreground text-xs font-bold hover:text-foreground hover:bg-card/10 transition-colors flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Previous
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => changePage(page)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors ${
                  currentPage === page
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-card/10 hover:text-foreground"
                }`}
              >
                {page}
              </button>
            ))}
          </div>
          <button
            onClick={() => changePage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="h-9 px-4 rounded-xl bg-card/5 text-muted-foreground text-xs font-bold hover:text-foreground hover:bg-card/10 transition-colors flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Next <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default LibraryFeed;
